import { GalaxyConfig, StarData, StarPosition } from 'types/galaxy';

// "AI Mind" view — the galaxy laid out by the behavioral embedding model.
// The server sends per-star normalized coordinates in [-1, 1] (UMAP projection
// of wallet embeddings). Similar traders land next to each other, so clusters
// on screen are real behavioral archetypes, not decoration.
//
// Stars without coordinates (wallets outside the model population) go to a
// dim outer ring. Their placement is a deterministic hash of the wallet so
// positions are stable across view switches (no Math.random flicker).

const XZ_FIT = 1.0; // fraction of galaxyRadius the map occupies
const Y_FLATTEN = 0.5; // compress vertical axis to keep the galaxy-disc look
const OUTER_RING = 1.32; // unmapped-star ring radius, × galaxyRadius
const OUTER_SPREAD = 10; // unmapped-star ring thickness

const hash01 = (s: string, seed: number): number => {
  let h = 2166136261 ^ seed;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
};

/** Map normalized [-1,1] mind coords to scene space (shared with guest stars). */
export const mindPositionFor = (
  m: { x: number; y: number; z: number },
  config: GalaxyConfig
): StarPosition => {
  const scale = config.galaxyRadius * XZ_FIT;
  return { x: m.x * scale, y: m.y * scale * Y_FLATTEN, z: m.z * scale };
};

/** Deterministic outer-ring slot for wallets without mind coords. */
export const outerRingPositionFor = (wallet: string, config: GalaxyConfig): StarPosition => {
  const a = hash01(wallet, 1) * Math.PI * 2;
  const r = config.galaxyRadius * OUTER_RING + (hash01(wallet, 2) - 0.5) * OUTER_SPREAD;
  return {
    x: Math.cos(a) * r,
    y: (hash01(wallet, 3) - 0.5) * 6,
    z: Math.sin(a) * r,
  };
};

export const calculateMindPositions = (
  starsData: StarData[],
  config: GalaxyConfig
): StarPosition[] => {
  return starsData.map((star) => {
    const m = star.mind;
    if (m) {
      return mindPositionFor(m, config);
    }
    return outerRingPositionFor(star.wallet, config);
  });
};
